import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Row, Col, Spinner } from 'react-bootstrap';
import { fetchAuditLogs as fetchAuditLogsAPI } from '../../../actioncreators/actioncreators';
import './SuperAdmin.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Single source of truth for all query params — page resets happen atomically
  // with filter changes so the effect always fires with consistent state.
  const [query, setQuery] = useState({
    action_type: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
  });
  const abortRef = useRef(null);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Strip dates with out-of-range years before sending to backend.
    const isValidDate = (d) => {
      if (!d) return true;
      const year = parseInt(d.split('-')[0], 10);
      return year >= 1900 && year <= 2100 && d.split('-')[0].length === 4;
    };

    const run = async () => {
      setLoading(true);
      try {
        const response = await fetchAuditLogsAPI({
          action_type: query.action_type,
          start_date: isValidDate(query.start_date) ? query.start_date : '',
          end_date: isValidDate(query.end_date) ? query.end_date : '',
          page: query.page,
          limit: query.limit,
        });
        if (controller.signal.aborted) return;
        const data = response.data?.data || {};
        const newTotal = data.total || 0;
        const newTotalPages = data.total_pages || 1;
        setLogs(Array.isArray(data.logs) ? data.logs : []);
        setTotal(newTotal);
        setTotalPages(newTotalPages);
        // Guard: if backend says current page is beyond new total, clamp to last page.
        // This happens when a filter narrows results while user was on a high page.
        if (query.page > newTotalPages && newTotalPages > 0) {
          setQuery(prev => ({ ...prev, page: newTotalPages }));
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch audit logs:', error);
        setLogs([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, [query]);

  // Clamp the year segment of a YYYY-MM-DD string to 4 digits max.
  const clampDateYear = (value) => {
    if (!value) return value;
    const parts = value.split('-');
    if (parts[0] && parts[0].length > 4) parts[0] = parts[0].slice(0, 4);
    return parts.join('-');
  };

  // Block a 5th digit being typed into the year segment of a date input.
  const handleDateKeyDown = (e, currentValue) => {
    if (!/^\d$/.test(e.key)) return;
    if (!currentValue) return;
    const parts = currentValue.split('-');
    const cursorPos = e.target.selectionStart;
    if (cursorPos <= 4 && parts[0] && parts[0].length >= 4) e.preventDefault();
  };

  // Filter change: always reset to page 1 atomically in one setState call
  const handleFilterChange = useCallback((field, value) => {
    setQuery(prev => ({ ...prev, [field]: value, page: 1 }));
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setQuery(prev => ({ ...prev, limit: Number(newLimit), page: 1 }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setQuery(prev => ({ ...prev, page: newPage }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setQuery(prev => ({
      ...prev,
      action_type: '',
      start_date: '',
      end_date: '',
      page: 1,
    }));
  }, []);

  const getActionColor = (action) => {
    const map = {
      SUSPEND_ORG: '#ef4444',
      ACTIVATE_ORG: '#10b981',
      CHANGE_PLAN: '#f59e0b',
      TOGGLE_FEATURE: '#3b82f6',
      DISABLE_USER: '#ef4444',
      FORCE_LOGOUT: '#f59e0b',
      CREATE_PLAN: '#8b5cf6',
      UPDATE_PLAN: '#6366f1',
      CREATE_ORG: '#0ea5e9',
    };
    return map[action] || '#6b7280';
  };

  const getActionLabel = (action) => {
    const map = {
      SUSPEND_ORG: 'Suspend Org',
      ACTIVATE_ORG: 'Activate Org',
      CHANGE_PLAN: 'Change Plan',
      TOGGLE_FEATURE: 'Toggle Feature',
      DISABLE_USER: 'Disable User',
      FORCE_LOGOUT: 'Force Logout',
      CREATE_PLAN: 'Create Plan',
      UPDATE_PLAN: 'Update Plan',
      CREATE_ORG: 'Create Org',
    };
    return map[action] || action;
  };

  const formatDate = (timestamp) => {
    const d = new Date(timestamp);
    return {
      date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
      time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' }),
    };
  };

  // Build the visible page-number window (at most 5 buttons around current page)
  const getPageWindow = () => {
    const WING = 2;
    let start = Math.max(1, query.page - WING);
    let end = Math.min(totalPages, query.page + WING);
    // Stretch the window if it's shorter than 5 at the edges
    if (end - start < 4) {
      if (start === 1) end = Math.min(totalPages, start + 4);
      else start = Math.max(1, end - 4);
    }
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const firstRecord = total === 0 ? 0 : (query.page - 1) * query.limit + 1;
  const lastRecord = Math.min(query.page * query.limit, total);
  const hasActiveFilter = query.action_type || query.start_date || query.end_date;
  const pageWindow = getPageWindow();
  const isFirstPage = query.page === 1;
  const isLastPage = query.page >= totalPages;

  return (
    <div className="audit-logs-container">

      {/* Filters */}
      <div className="audit-logs-filters">
        <Row className="g-2 align-items-end">
          <Col lg={3} md={6} sm={12}>
            <Form.Group>
              <Form.Label className="filter-label">Action Type</Form.Label>
              <Form.Select
                className="filter-select"
                value={query.action_type}
                onChange={(e) => handleFilterChange('action_type', e.target.value)}
              >
                <option value="">All Actions</option>
                <option value="SUSPEND_ORG">Suspend Org</option>
                <option value="ACTIVATE_ORG">Activate Org</option>
                <option value="CHANGE_PLAN">Change Plan</option>
                <option value="CREATE_PLAN">Create Plan</option>
                <option value="UPDATE_PLAN">Update Plan</option>
                <option value="TOGGLE_FEATURE">Toggle Feature</option>
                <option value="DISABLE_USER">Disable User</option>
                <option value="FORCE_LOGOUT">Force Logout</option>
                <option value="CREATE_ORG">Create Org</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <Form.Group>
              <Form.Label className="filter-label">Start Date</Form.Label>
              <Form.Control
                className="filter-input"
                type="date"
                value={query.start_date}
                min="1900-01-01"
                max="2100-12-31"
                onKeyDown={(e) => handleDateKeyDown(e, query.start_date)}
                onChange={(e) => handleFilterChange('start_date', clampDateYear(e.target.value))}
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <Form.Group>
              <Form.Label className="filter-label">End Date</Form.Label>
              <Form.Control
                className="filter-input"
                type="date"
                value={query.end_date}
                min="1900-01-01"
                max="2100-12-31"
                onKeyDown={(e) => handleDateKeyDown(e, query.end_date)}
                onChange={(e) => handleFilterChange('end_date', clampDateYear(e.target.value))}
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <div className="al-filter-actions">
              {hasActiveFilter && (
                <button className="al-clear-btn" onClick={handleClearFilters}>
                  Clear Filters
                </button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Table */}
      <div className="audit-logs-table-wrapper">
        {loading ? (
          <div className="audit-logs-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="audit-logs-empty">
            <p>{hasActiveFilter ? 'No audit logs match the selected filters.' : 'No audit logs found.'}</p>
          </div>
        ) : (
          <>
            <div className="audit-logs-table">
              <div className="table-header">
                <div className="col-timestamp">Time</div>
                <div className="col-actor">Actor</div>
                <div className="col-action">Action</div>
                <div className="col-target">Target</div>
                <div className="col-ip">IP Address</div>
              </div>
              <div className="table-body">
                {logs.map((log) => (
                  <div className="table-row" key={log.log_id}>
                    <div className="col-timestamp">
                      <div className="timestamp-date">{formatDate(log.timestamp).date}</div>
                      <div className="timestamp-time">{formatDate(log.timestamp).time}</div>
                    </div>
                    <div className="col-actor">
                      <div className="actor-name">{log.admin_name || 'Unknown'}</div>
                      <div className="actor-email">{log.admin_email}</div>
                    </div>
                    <div className="col-action">
                      <span
                        className="action-badge"
                        style={{ backgroundColor: getActionColor(log.action_type) }}
                      >
                        {getActionLabel(log.action_type)}
                      </span>
                    </div>
                    <div className="col-target">
                      <div className="target-name">{log.target_org_name || 'N/A'}</div>
                      <div className="target-type">{log.target_entity_type}</div>
                    </div>
                    <div className="col-ip">
                      <span className="ip-address">{log.ip_address}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer: record summary + page size + pagination */}
            <div className="audit-logs-footer">
              <div className="al-footer-left">
                <span className="al-record-summary">
                  {total === 0
                    ? 'No records'
                    : <>Showing <strong>{firstRecord}–{lastRecord}</strong> of <strong>{total}</strong> records</>
                  }
                </span>
                <div className="al-page-size">
                  <span className="al-page-size-label">Rows per page:</span>
                  <select
                    className="al-page-size-select"
                    value={query.limit}
                    onChange={(e) => handleLimitChange(e.target.value)}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="al-pagination">
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(1)}
                  disabled={isFirstPage}
                  title="First page"
                >
                  ««
                </button>
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(query.page - 1)}
                  disabled={isFirstPage}
                  title="Previous page"
                >
                  ‹ Prev
                </button>

                {pageWindow[0] > 1 && (
                  <>
                    <button className="al-page-btn" onClick={() => handlePageChange(1)}>1</button>
                    {pageWindow[0] > 2 && <span className="al-page-ellipsis">…</span>}
                  </>
                )}

                {pageWindow.map(n => (
                  <button
                    key={n}
                    className={`al-page-btn${n === query.page ? ' al-page-btn--active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >
                    {n}
                  </button>
                ))}

                {pageWindow.at(-1) < totalPages && (
                  <>
                    {pageWindow.at(-1) < totalPages - 1 && <span className="al-page-ellipsis">…</span>}
                    <button className="al-page-btn" onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(query.page + 1)}
                  disabled={isLastPage}
                  title="Next page"
                >
                  Next ›
                </button>
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={isLastPage}
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
